import { ChangeDetectionStrategy, Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import {
  combineLatest,
  map,
  BehaviorSubject,
  from,
  shareReplay,
  startWith,
  Subscription,
  distinctUntilChanged,
  debounceTime
} from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { WorkspaceCardComponent } from './workspace-card/workspace-card.component';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Workspace } from '@workspaces/models/workspaces';
import { AppState } from '../../../state/app.state';
import { Store } from '@ngrx/store';
import { workspaceListFeature, WorkspaceListFetch } from '@workspaces/store/workspace-list.feature';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CognitoService } from 'src/app/services/cognito.service';
import { WorkspaceListElementComponent } from './workspace-list-element/workspace-list-element.component';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSlideToggle } from '@angular/material/slide-toggle';

export const SORT_VALUES = [
  { id: 'name', text: 'Name' },
  { id: 'state', text: 'Status' },
  { id: 'description', text: 'Description' },
  { id: 'last_activity', text: 'Last activity' },
  { id: 'members', text: 'Number of members' }
] as const;

type SortValueId = (typeof SORT_VALUES)[number]['id'];
interface SearchFormValue {
  field: string | null;
  select: SortValueId | null;
  onlyMine: boolean;
}
interface PaginationState {
  pageIndex: number;
  pageSize: number;
}
type WorkpaceWithIsMember = Workspace & { isMember: boolean };

@Component({
  selector: 'app-workspace-list',
  templateUrl: './workspace-list.component.html',
  styleUrls: ['./workspace-list.component.scss', '../../global-workspace-styles.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    AsyncPipe,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    WorkspaceCardComponent,
    RouterModule,
    MatProgressSpinner,
    WorkspaceListElementComponent,
    MatPaginator,
    MatSlideToggle,
    MatMenuModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkspaceListComponent implements OnInit, OnDestroy {
  public searchForm = new FormGroup({
    field: new FormControl<string>(''),
    select: new FormControl<SortValueId | null>(null),
    onlyMine: new FormControl<boolean>(false)
  });
  public readonly sortValues = SORT_VALUES;
  public isListView = false;
  private readonly sub = new Subscription();

  public static DEFAULT_PAGE_INDEX = 0;
  public static DEFAULT_PAGE_SIZE = 10;
  public static PAGE_SIZE_OPTIONS = [5, 10, 25, 100];

  public pageSizeOptions = WorkspaceListComponent.PAGE_SIZE_OPTIONS;
  public paginatorTrigger$ = new BehaviorSubject<PaginationState>({
    pageIndex: WorkspaceListComponent.DEFAULT_PAGE_INDEX,
    pageSize: WorkspaceListComponent.DEFAULT_PAGE_SIZE
  });

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly store: Store<AppState>,
    protected readonly cognitoService: CognitoService
  ) {
    this.fetchWorkspaces();
  }

  public workspaceIsLoading$ = this.store.select(workspaceListFeature.workspaceListsIsLoading);

  private workspacesWithMembership = combineLatest([
    this.store.select(workspaceListFeature.selectData),
    from(this.cognitoService.getUserId())
  ]).pipe(
    map(([workspaceList, userId]) => this.mapMemberWorkspace(workspaceList, userId)),
    // reuse result for multiple subscriptions
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public searchFilteredWorkspaces$ = combineLatest([
    this.workspacesWithMembership,
    this.searchForm.valueChanges.pipe(startWith(this.searchForm.value))
  ]).pipe(
    map(([workspaceList, form]) => {
      const field = form?.field ?? '';
      const select = form?.select ?? null;
      const onlyMine = form?.onlyMine ?? false;
      const filteredList = this.filterWorkSpaces(workspaceList, field);
      const sortWorkSpaces = this.sortWorkSpaces(filteredList, select);
      const onlyMineWorkSpaces = this.getOnlyMineWorkspaces(sortWorkSpaces, onlyMine);
      return onlyMineWorkSpaces;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public totalWorkspacesLength$ = this.searchFilteredWorkspaces$.pipe(map(list => list.length));

  public pagedWorkspaces$ = combineLatest([this.searchFilteredWorkspaces$, this.paginatorTrigger$.asObservable()]).pipe(
    map(([filteredList, paginator]) => {
      const startIndex = paginator.pageIndex * paginator.pageSize;
      const endIndex = startIndex + paginator.pageSize;
      return filteredList.slice(startIndex, endIndex);
    })
  );

  async ngOnInit(): Promise<void> {
    // subscribe to form changes to update URL & reset paginator
    this.sub.add(
      this.searchForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(300)).subscribe(filter => {
        this.onFilterChange(filter as SearchFormValue);
      })
    );

    setTimeout(() => this.initializeFormFromUrl(), 100);
  }

  async fetchWorkspaces() {
    const isAdmin = await this.cognitoService.isAdmin();
    this.store.dispatch(WorkspaceListFetch({ include_archived: true, include_deleted: isAdmin }));
  }

  private initializeFormFromUrl(): void {
    const params = this.route.snapshot.queryParams;
    const initialField = ((params['field'] || '') as SearchFormValue['field']) || null;
    const initialSelect = (params['select'] as SearchFormValue['select']) || null;
    const stringInitialOnlyMine: string | null = params['onlyMine'] || null;

    const initialOnlyMine = stringInitialOnlyMine === 'true';
    const urlPageIndex = parseInt(params['page'], 10);
    const urlPageSize = parseInt(params['size'], 10);

    const pageIndex =
      !isNaN(urlPageIndex) && urlPageIndex >= 0 ? urlPageIndex : WorkspaceListComponent.DEFAULT_PAGE_INDEX;
    const pageSize =
      !isNaN(urlPageSize) && WorkspaceListComponent.PAGE_SIZE_OPTIONS.includes(urlPageSize)
        ? urlPageSize
        : WorkspaceListComponent.DEFAULT_PAGE_SIZE;
    const initialState: SearchFormValue = {
      field: initialField,
      select: initialSelect,
      onlyMine: !!initialOnlyMine
    };

    this.paginatorTrigger$.next({ pageIndex: pageIndex, pageSize: pageSize });
    this.searchForm.patchValue(initialState);
  }

  public onFilterChange(formValue: SearchFormValue): void {
    const newPageIndex = 0;

    this.paginatorTrigger$.next({ pageIndex: newPageIndex, pageSize: this.paginatorTrigger$.value.pageSize });
    this.updateUrl(formValue, newPageIndex, this.paginatorTrigger$.value.pageSize);
  }

  public handlePageEvent(event: PageEvent): void {
    this.updateUrl(this.searchForm.value as SearchFormValue, event.pageIndex, event.pageSize);
    this.paginatorTrigger$.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  private updateUrl(formValue: SearchFormValue, pageIndex: number, pageSize: number): void {
    const queryParams: Record<string, string | null> = {};

    if (formValue.field) {
      queryParams['field'] = formValue.field;
    } else {
      delete queryParams['field'];
    }

    if (formValue.select) {
      queryParams['select'] = formValue.select;
    } else {
      delete queryParams['select'];
    }

    if (formValue.onlyMine) {
      queryParams['onlyMine'] = `${formValue.onlyMine}`;
    } else {
      delete queryParams['onlyMine'];
    }

    if (pageIndex !== WorkspaceListComponent.DEFAULT_PAGE_INDEX) {
      queryParams['page'] = pageIndex.toString();
    } else {
      delete queryParams['page'];
    }

    if (pageSize !== WorkspaceListComponent.DEFAULT_PAGE_SIZE) {
      queryParams['size'] = pageSize.toString();
    } else {
      delete queryParams['size'];
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'replace',
      replaceUrl: true
    });
  }

  private mapMemberWorkspace(workspaceList: Workspace[], userId: string) {
    return workspaceList.map(workspace => {
      const isWorkspaceUser = workspace.members.find(member => member.id === userId || member.cognito_id === userId);
      const activeUser = isWorkspaceUser?.invitation_status.toLowerCase() === 'active';
      return { ...workspace, isMember: activeUser };
    });
  }

  private filterWorkSpaces(workspaceList: WorkpaceWithIsMember[], searchText?: string | null) {
    if (!searchText?.length) {
      return workspaceList;
    }
    const normalisedSearchText = searchText.toLowerCase();

    const stringFilteredWorkspaces = workspaceList.filter(
      workspaceElement =>
        workspaceElement.description.toLowerCase().includes(normalisedSearchText) ||
        workspaceElement.name.toLowerCase().includes(normalisedSearchText)
    );

    return stringFilteredWorkspaces;
  }

  private getOnlyMineWorkspaces(workspaceList: WorkpaceWithIsMember[], onlyMine: boolean) {
    if (!onlyMine) {
      return workspaceList;
    }
    return workspaceList.filter(workspaceElement => workspaceElement.isMember);
  }

  private sortWorkSpaces(workspaceList: WorkpaceWithIsMember[], sortOption?: SortValueId | null) {
    return [...workspaceList].sort((a, b) => {
      if (sortOption === 'description') {
        return a.description > b.description ? 1 : b.description > a.description ? -1 : 0;
      } else if (sortOption === 'name') {
        return a.name > b.name ? 1 : b.name > a.name ? -1 : 0;
      } else if (sortOption === 'members') {
        return a.members.length - b.members.length;
      } else if (sortOption === 'state') {
        return a.state > b.state ? 1 : b.state > a.state ? -1 : 0;
      } else if (sortOption === 'last_activity') {
        return new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime();
      } else return 0;
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
